import { Component, ElementRef, inject, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TreeNode } from 'primeng/api';

import { cloneDeep, difference } from 'lodash';

import { HisSessionStorageKeys } from '../../core/model/his-session-storage-keys';
import { HisConfirmationService } from '../../core/service/base/his-confirmation.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisRouteService } from '../../core/service/routing/his-route.service';
import { HisOption } from '../model/his-option';
import { HisSearchConfiguration } from '../model/his-search-configuration';
import { HisSearchCriteriaSet } from '../model/his-search-criteria-set';
import { HisSearchCriteriaSetRequest } from '../model/his-search-criteria-set-request';
import { HisSearchCriteriaSetStateInfo } from '../model/his-search-criteria-set-state-info';
import { HisSearchCriteriaSetStateType } from '../model/his-search-criteria-set-state-type';
import { HisWidgetBase } from '../model/his-widget-base';
import { HisWidgetGroup } from '../model/his-widget-group';
import { HisMultiSelectWidget } from '../model/his-widget-multiselect';
import { HisSearchSelectWidget } from '../model/his-widget-search-select';
import { HisSelectWidget } from '../model/his-widget-select';
import { HisTreeSelectWidget } from '../model/his-widget-tree-select';
import { HisSearchCriteriaSetService } from '../service/his-search-criteria-set.service';
import { HisSearchService } from '../service/his-search.service';
import { HisWidgetService } from '../service/his-widget.service';
import { HisWidgetUtils } from '../utils/his-widget-utils';

import { CONFIRM_PDIALOG_KEY } from '../../core/component/confirmdialog/his-confirm-dialog.component';

import { HisArrayUtils, HisStringUtils } from '../../core/utils';
import { HisWidgetBaseSelect } from '../model/his-widget-base-select';
import { HisOptionWidget } from '../model/his-widget-option';

import { AutoComplete, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { map, Observable, of, Subject, tap, throttleTime } from 'rxjs';
import { OptionByIdRequest } from '../model/options/option-by-id-request';
import { HisSearch, HisSearchCriteria } from '../model/search/his-search';
import { HisSearchParserHelper } from '../model/search/his-search-parser-helper';
import { GenericSearchUtil } from '../utils/generic-search-util';
import { HisSearchFormData } from './his-search-form-data';
import { HisSearchFormComponentBase } from './his-search-form.component';

@Component( {
    selector: 'his-search-save-params',
    templateUrl: './his-search-save-params.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchSaveParamsComponent implements OnInit {
    private readonly searchCriteriaSetService = inject( HisSearchCriteriaSetService );
    private readonly notificationService = inject( HisNotificationService );
    private readonly hisUiService = inject( HisUiService );
    private readonly confirmationService = inject( HisConfirmationService );
    private readonly widgetService = inject( HisWidgetService );
    private readonly routeService = inject( HisRouteService );
    private readonly searchService = inject( HisSearchService );


    @Input() configFile: string;
    @Input() form: UntypedFormGroup;
    @Input() widgetGroups: HisWidgetGroup[];
    @Input() searchFormData: HisSearchFormData;
    @Input() searchForm: HisSearchFormComponentBase;
    @Input() callerId: string;
    public currentSearchCriteriaSets: HisSearchCriteriaSet[] = [];
    public allSearchCriteriaSets: HisSearchCriteriaSet[] = [];
    public newSearchCriteriaSetName: string;
    public selectedSearchSet: HisSearchCriteriaSet;

    isShowAddView = false;

    isShowSetStandardIcon = false;
    isShowResetStandardIcon = false;

    isShowDeleteIcon = false;
    isShowAddIcon = false;
    isShowSaveIcon = false;
    isShowSelectField = false;
    isShowAddLink = false;

    isDataArrived = false;

    private static readonly STANDARD = ' <Standard>';

    private selectedValueBeforCancel: HisSearchCriteriaSet;

    private componentId: string;

    private stateInfo = new HisSearchCriteriaSetStateInfo();

    /** Buffer used to coalesce calls of `showMessageCouldNotLoadAllCriteria()` into a single call to only show one notification. */
    private couldNotLoadAllCriteriaMessageBuffer$ = new Subject<void>();

    @ViewChild( 'autocomplete' ) autocomplete: AutoComplete;

    /**
     * Für diese Komponente gibt es eine innere und eine aeußere Navigation.
     *
     * Die innere Navigation steuert den Ablauf innerhalb der Komponente,
     * also z. B. welche Buttons werden in welchem Zusand angezeigt. Dies
     * wird beim Eintritt in die Komponente in der Methode getSearchCriteriaSets gesteuert.
     *
     * Existiert die Komponente bereits, dann findet diese Steuerung mittels onSelect oder
     * auch delete, add usw. statt.
     *
     * Die aeußere Navigation wird durch die generische Suche gesteuert,
     * also z. B. durch die Button "Neue Suche", "Suche aendern"
     * Dies findet in der Methode controlSearchFormEntry statt. Dabei ist zu beachten,
     * dass dort keine Steuerung der Buttons erfolgen sollte, da diese in nachgelagerten
     * Methoden sattfindet und sich deshalb im Zweifelsfall nicht auswirken wuerden.
     */
    ngOnInit() {
        this.initializeResetInputs();
        this.setComponentId();

        // Aktuell ist noch kein context in der generischen Suche implementiert
        let context = null;

        let searchCriteriaRequest = new HisSearchCriteriaSetRequest( this.configFile, context, this.componentId );
        this.getSearchCriteriaSetsAndRegulateTheInitialView( searchCriteriaRequest );
        this.couldNotLoadAllCriteriaMessageBuffer$.pipe( throttleTime( 1000 ) ).subscribe( () => this.showMessageCouldNotLoadAllCriteria() );
    }

    private setComponentId() {

        let lastSearchCriteriaSetComponentIdFromSession = sessionStorage.getItem(
            HisSessionStorageKeys.CS_SYS_SEARCH_CRITERIA_SET_COMPONENT_ID );
        let isIdFromSession = !HisStringUtils.isBlank( lastSearchCriteriaSetComponentIdFromSession );

        // Alle Fälle, die von der Suchergebnisseite kommen
        if ( isIdFromSession && ( this.isNewSearch() || this.isChangeSearch() ) ) {
            this.componentId = lastSearchCriteriaSetComponentIdFromSession;
            return;
        }

        //workaround: we should not use component.name in production mode
        this.componentId = this.callerId ?? this.searchCriteriaSetService.getFlowNameForSearchCriteriaSets();

        sessionStorage.setItem(
            HisSessionStorageKeys.CS_SYS_SEARCH_CRITERIA_SET_COMPONENT_ID,
            this.componentId );
        return;
    }

    /**
     * Reagiert auf die Auswahl aus dem Auswahlfeld. Läd das ausgewaehlte Suchset
     * und ändert ggf die Buttons für "Standard"
     *
     * @param value the selected HisSearchCriteriaSet
     */
    public onSelect( event: AutoCompleteSelectEvent ) {
        let value = event.value as HisSearchCriteriaSet;
        this.notificationService.clear();

        console.log( 'his-search-save-params.component#onSelect', this.selectedSearchSet );
        if ( value === null || value === undefined ) {
            return;
        }

        let isModified = this.isFormModified();
        if ( !isModified ) {
            if ( value.emptyOption ) {
                this.selectEmptyOption( value );
                return;
            }
            this.selectSearchSet( value );
            return;
        }

        // Wenn es bereits manuelle Änderungen im Formular durch Nutzer gab
        // Siehe Hiszilla 247517
        this.confirmationService.confirm( {
            key: CONFIRM_PDIALOG_KEY,
            message: this.hisUiService.translate(
                'genericSearch.searchCriteriaSets.unsavedChangesQuestion' ),
            header: this.hisUiService.translate(
                'cs.sys.notification.Notification.successType' ),
            accept: () => {

                if ( value.emptyOption ) {
                    this.selectEmptyOption( value );
                    return;
                }
                this.selectSearchSet( value );

            },
            reject: () => {
                console.log(
                    'his-search-save-params.component#onSelect current search set on reject:', this.stateInfo.currentCriteriaSet );
                value = this.stateInfo.currentCriteriaSet;
                this.selectedSearchSet = this.stateInfo.currentCriteriaSet;
                return;
            }
        } );
    }

    isFormModified(): boolean {
        for ( let group of this.widgetGroups ) {
            for ( let widget of group.widgets ) {
                if ( widget.isWidgetModified() ) {
                    return true;
                }
            }
        }

        return false;
    }

    private selectEmptyOption( value: HisSearchCriteriaSet ) {
        this.storeSearchSetAtSessionStorage( value );

        // model zurücksetzen
        this.resetInputs();

        // originalValue neu ausrichten
        this.setWidgetOriginalValueFromWidgetValue( true );

        // Das Auswahlfeld für Suchsets muss extra zurueckgesetzt werden,
        // da es nicht Bestandteil der form, bzw. des models ist
        this.searchService.setResetInputs( true );
        this.showSearchSetExistsButNothingSelectedView();

        this.searchFormData.resetWidgetsToDisplayFromSearchSet();
    }

    private selectSearchSet( value: HisSearchCriteriaSet ) {
        this.selectedSearchSet = value;
        this.storeSearchSetAtSessionStorage( value );

        // Wird beim Laden durchgefuehrt:
        // this.resetInputs();
        // this.searchService.setResetInputs( true );
        this.loadSearchSet( value );

        this.showSelectView();

        this.isShowSetStandardIcon = !value.standard;
        this.isShowResetStandardIcon = value.standard;
    }

    addSearchCriteriaSet() {
        this.notificationService.clear();
        this.stateInfo.stateType = HisSearchCriteriaSetStateType.ADDED;
        this.stateInfo.currentCriteriaSet = null;
        this.selectedValueBeforCancel = this.stateInfo.currentCriteriaSet;

        this.selectedSearchSet = null;

        // this.isShowSelectView = false;
        this.isShowDeleteIcon = false;
        this.isShowAddIcon = false;
        this.isShowAddLink = false;
        this.isShowSelectField = false;

        this.isShowSaveIcon = true;
        this.isShowAddView = true;

        this.isShowSetStandardIcon = false;
        this.isShowResetStandardIcon = false;
        this.isShowAddLink = false;
    }

    saveSearchCriteriaSet() {
        let searchSetToSave = this.selectedSearchSet;

        // Es muss ausgewertet werden, ob ein Suchset bereits geladen ist.
        // Siehe auch hiszilla 244933
        let isLoaded = this.stateInfo.stateType === HisSearchCriteriaSetStateType.LOADED || HisSearchCriteriaSetStateType.CHANGED_SEARCH;

        // Um Update durchzuführen muss ein Suchset bereits gespeichert sein
        // Siehe auch hiszilla 245707
        if ( isLoaded && searchSetToSave !== this.stateInfo.currentCriteriaSet ) {
            searchSetToSave = this.stateInfo.currentCriteriaSet;
        }

        let isAlreadySaved =
            searchSetToSave !== null &&
            searchSetToSave !== undefined &&
            searchSetToSave.id !== null &&
            !HisStringUtils.isBlank( searchSetToSave.defaulttext );

        if ( isAlreadySaved && isLoaded ) {
            this.update( this.stateInfo.currentCriteriaSet );
        } else {
            this.save();
        }

        this.setWidgetOriginalValueFromWidgetValue( false );

        this.setAutocompleteInputFocus();
    }

    private setAutocompleteInputFocus() {
        setTimeout( () => {
            if ( this.autocomplete ) {
                console.log( 'his-search-save-params.component#setAutocompleteInputFocus()', this.autocomplete );
                if ( this.autocomplete.multiple ) {
                    ( this.autocomplete.multiInputEl as ElementRef ).nativeElement.focus();
                }
                else {
                    this.autocomplete.inputEL?.nativeElement.focus();
                }
                return;
            }
        }, 100 );
    }

    /**
     * Neu Ausrichten von originalValue und originalNot
     *
     * @param reset Wenn dieser boolean Param auf true gesetzt wird,
     * dann werden alle original values auf null bzw. false gesetzt
     */
    setWidgetOriginalValueFromWidgetValue( reset: boolean ) {
        for ( let group of this.widgetGroups ) {
            for ( let widget of group.widgets ) {
                if ( reset ) {
                    widget.originalValue = null;
                } else {
                    // Im Fall von Array-Werten brauchen wir unterschiedliche Objekte,
                    // damit Änderungen an value nicht gleich Änderungen bei originalValue sind
                    widget.originalValue = Array.isArray( widget.value ) ? [...widget.value] : widget.value;
                }

                if ( widget instanceof HisWidgetBaseSelect ) {
                    widget.originalNot = reset ? false : widget.not;
                }
            }
        }
    }

    save() {
        this.notificationService.clear();
        let info = this.stateInfo;
        if ( info.currentCriteriaSet === null || info.currentCriteriaSet.id === null ) {

            // Speichern leerer Suchsetnamen verhindern
            if ( HisStringUtils.isBlank( this.newSearchCriteriaSetName ) ) {
                this.notificationService.error(
                    this.hisUiService.translate( 'genericSearch.searchCriteriaSets.saveProblemNoNameGiven', this.newSearchCriteriaSetName ),
                    this.hisUiService.translate( 'view.messages.error' ) );
                return;
            }

            // doppelte und verbotene Suchsetnamen verhindern
            for ( let criteriaSet of this.allSearchCriteriaSets ) {

                if ( criteriaSet.emptyOption ) {
                    continue;
                }

                // hier noch Standard berücksichtigen
                let isEqalName = criteriaSet.defaulttext === this.newSearchCriteriaSetName;
                let isEqualToStandardSet = criteriaSet.defaulttext.replace( HisSearchSaveParamsComponent.STANDARD, '' ).trim()
                    === this.newSearchCriteriaSetName;

                // eventuell hier mehr unterschiedliche Meldungen das gibt es in JSF aber nicht
                if ( isEqalName || isEqualToStandardSet || criteriaSet.emptyOption ) {
                    this.notificationService.error(
                        this.hisUiService.translate( 'genericSearch.searchCriteriaSets.duplicateNameError', this.newSearchCriteriaSetName ),
                        this.hisUiService.translate( 'view.messages.error' ) );
                    // undefined oder null, was ist hier richtig?
                    this.newSearchCriteriaSetName = null;
                    return;
                }
            }

            this.notificationService.clear();

            // Es ist hier ein noch nicht in der DB gespeicherter Wert und hat deshalb keine Id
            let id = null;

            // Context ist noch nicht implementiert
            let context = null;

            // Standard true kann erst vergeben werden, wenn das Suchset jemals gespeichert war
            let isStandard = false;
            let search: HisSearch = HisSearchParserHelper.createNewHisSearchByWidgetValues( this.widgetGroups, this.configFile );
            console.log( 'his-search-save-params.component#save()', '#search', search );

            let searchCriteriaSetToSave: HisSearchCriteriaSet = new HisSearchCriteriaSet(
                id,
                HisStringUtils.trimAll( this.newSearchCriteriaSetName ),
                this.configFile,
                context,
                this.componentId,
                isStandard,
                search );

            this.searchCriteriaSetService.saveSearchCriteriaSet( searchCriteriaSetToSave ).subscribe(
                ( savedSearchCriteriaSet: HisSearchCriteriaSet ) => {
                    if ( savedSearchCriteriaSet != null ) {
                        // Wenn das Suchset das erste ist, dann eine leere Option hinzufügen
                        console.log( 'allSearchCriteriaSets', this.allSearchCriteriaSets.length );
                        console.log( 'currentSearchCriteriaSets', this.currentSearchCriteriaSets.length );
                        if ( this.allSearchCriteriaSets.length === 0 && this.currentSearchCriteriaSets.length === 0 ) {
                            this.addEmptyOption();
                        }

                        this.allSearchCriteriaSets.push( savedSearchCriteriaSet );
                        this.currentSearchCriteriaSets.push( savedSearchCriteriaSet );

                        this.notificationService.success(
                            this.hisUiService.translate( 'cs.sys.portal.portlet.portalmessages.save' ),
                            this.hisUiService.translate( 'view.messages.success' ) );

                        this.showSelectView();

                        // Da das hier gespeicherte Suchset immer neu ist
                        // unterscheidet es sich hier vom Update
                        this.isShowSetStandardIcon = true;
                        this.isShowResetStandardIcon = false;

                        this.selectedSearchSet = savedSearchCriteriaSet;
                        this.storeSearchSetAtSessionStorage( savedSearchCriteriaSet );
                        this.newSearchCriteriaSetName = ''; // clear input

                        // Wenn ein Suchset gespeichert ist,
                        // dann sollte der Zustand so sein, wie enn es geladen ist
                        this.stateInfo.currentCriteriaSet = savedSearchCriteriaSet;

                        // Hier ist 'gespeichert' mit 'geladen' gleichzusetzen
                        this.stateInfo.stateType = HisSearchCriteriaSetStateType.LOADED;
                    }
                }
            );
            return;
        }
    }

    update( loadedCriteriaSet: HisSearchCriteriaSet ) {
        this.notificationService.clear();
        if ( loadedCriteriaSet === null ) {
            throw new Error( 'HisSearchCriteriaSet to update must not be null' );
        }
        if ( loadedCriteriaSet.id === null ) {
            throw new Error( 'HisSearchCriteriaSet to update must not have null id' );
        }

        let search: HisSearch = HisSearchParserHelper.createNewHisSearchByWidgetValues( this.widgetGroups, this.configFile );
        loadedCriteriaSet.search = search;

        this.searchCriteriaSetService.updateSearchCriteriaSet( loadedCriteriaSet ).subscribe(
            ( updatedSearchCriteriaSet: HisSearchCriteriaSet ) => {
                if ( updatedSearchCriteriaSet ) {
                    this.notificationService.success(
                        this.hisUiService.translate( 'cs.sys.portal.portlet.portalmessages.save' ),
                        this.hisUiService.translate( 'view.messages.success' ) );

                    this.showSelectView();

                    // 1. Damit hier bei einer Eingabe auch das geladene Suchset angezeigt wird, Siehe Hizilla 244933
                    // 2. Falls das gespeicherte Suchset ein Standard Suchset ist,
                    // muss auch der Zusatz <Standard> angezeigt werden
                    let updatedSet = updatedSearchCriteriaSet as HisSearchCriteriaSet;
                    if ( updatedSet.standard ) {
                        updatedSet.defaulttext = updatedSet.defaulttext + HisSearchSaveParamsComponent.STANDARD;
                    }
                    this.stateInfo.currentCriteriaSet = updatedSet;

                    this.selectedSearchSet = this.stateInfo.currentCriteriaSet;

                    // altes Set aus Listen löschen
                    this.initAllSearchCriteriaSetsAndRemoveById( updatedSet.id );
                    this.initCurrentSearchCriteriaSetsAndRemoveById( updatedSet.id );

                    // geupdatetes Set hinzufügen
                    this.allSearchCriteriaSets.push( updatedSet );
                    this.currentSearchCriteriaSets.push( updatedSet );

                    this.isShowSetStandardIcon = !updatedSearchCriteriaSet.standard;
                    this.isShowResetStandardIcon = updatedSearchCriteriaSet.standard;
                }
            }
        );
    }

    private enrichSearchConfigurationWithSearchCriteria(
        searchConfiguration: HisSearchConfiguration,
        search: HisSearch
    ): HisSearchConfiguration {

        for ( let group of search.groups ) {
            for ( let criterion of group.criteria ) {
                this.enrichSearchConfigurationWithSearchCriterionValue( criterion, searchConfiguration );
            }
            for ( let criteriaGroup of group.criteriaGroups ) {
                for ( let criterion of criteriaGroup.criteria ) {
                    this.enrichSearchConfigurationWithSearchCriterionValue( criterion, searchConfiguration );
                }
            }
        }
        return searchConfiguration;
    }

    private enrichSearchConfigurationWithSearchCriterionValue(
        criterion: HisSearchCriteria,
        searchConfiguration: HisSearchConfiguration ) {

        for ( let group of searchConfiguration.searchCriteria.group ) {
            for ( let searchCriterion of group.criteria ) {
                // .endsWith: Kriteriengruppen: de.his.psv.suchePersonen[1]Person.gender.id
                if ( criterion.field.endsWith( searchCriterion.path ) && criterion.value ) {
                    searchCriterion.value = criterion.value;
                    searchCriterion.not = criterion.not;
                }
            }
        }
    }

    loadSearchSet( searchCriteriaSetToLoad: HisSearchCriteriaSet ) {
        // Setzt widget.value VOR resetInputs auf null
        // damit doPresetInputFields die Standardwerte aus der Konfiguration nicht erneut setzt
        this.searchFormData.clearAllWidgetValues();

        this.resetInputs();
        this.searchService.setResetInputs( true );
        this.selectedSearchSet = searchCriteriaSetToLoad;

        this.searchCriteriaSetService.loadCriteriaSet( searchCriteriaSetToLoad.id ).subscribe(
            ( search: HisSearch ) => {
                console.log( 'his-search-save-params.component#loadSearchSet() #search', search );
                if ( search ) {
                    search.configFile = this.configFile;
                    let originalSearchConfig = this.searchFormData.getSearchConfiguration();
                    let copyOfSearchConfig = this.getCopyOfSearchConfiguration( originalSearchConfig );

                    let enrichedSearchConfig = this.enrichSearchConfigurationWithSearchCriteria( copyOfSearchConfig, search );
                    let isValueInNonQuicksearchGroup = this.evaluateExtendedSearch( enrichedSearchConfig, search );
                    console.log( 'his-search-save-params.component#loadSearchSet() #enrichedSearchConfig: ', enrichedSearchConfig );

                    if ( isValueInNonQuicksearchGroup ) {
                        // hier werden nur die Options nachgeladen, das Anzeigen der Felder wird in his-search-form.html geregelt
                        // hier brauche ich dann den Übersetzunggschritt zu searchConfiguration;
                        this.widgetService.loadWidgetOptions( enrichedSearchConfig, true );
                    }

                    this.setWidgetGroups( search, searchCriteriaSetToLoad );

                    // originalValue neu ausrichten
                    this.setWidgetOriginalValueFromWidgetValue( false );

                    this.stateInfo.currentCriteriaSet = searchCriteriaSetToLoad;
                    this.stateInfo.stateType = HisSearchCriteriaSetStateType.LOADED;
                }
            } );
    }

    private getCopyOfSearchConfiguration( searchConfiguration: HisSearchConfiguration ): HisSearchConfiguration {
        let copyOfSearchConfig = new HisSearchConfiguration();
        copyOfSearchConfig.configFile = searchConfiguration.configFile;
        copyOfSearchConfig.name = searchConfiguration.name;
        copyOfSearchConfig.pageTitle = searchConfiguration.pageTitle;
        copyOfSearchConfig.searchCriteria = cloneDeep( searchConfiguration.searchCriteria );
        copyOfSearchConfig.tableTitle = searchConfiguration.tableTitle;

        return copyOfSearchConfig;
    }

    private evaluateExtendedSearch( searchConfiguration: HisSearchConfiguration, search: HisSearch ): boolean {
        // gespeicherte Werte, die in der erweiterten Suche stehen, müssen angezeigt werden
        // his-search-form.html mitteilen, dass weitere Felder
        // außer sie Schnellsuchfelder angezeigt werden sollen
        let isValueInNonQuicksearchGroup = false;
        let widgetIdsToShowExtendedCriteria: string[] = [];

        for ( let group of search.groups ) {
            for ( let criterion of group.criteria ) {
                console.log( 'his-search-save-params.component#evaluateExtendedSearch() #criterion', criterion );
                let searchCriterion = HisSearchConfiguration.getSearchCriterion( searchConfiguration, criterion.field );
                let searchGroup = HisSearchConfiguration.getGroup( searchConfiguration, criterion.field );

                // Fehlende widget options nachladen wenn
                // 1. es eine Quicksearch Gruppe ist und darin ein nicht quicksearch Feld ist, für das Werte gespeichert wurden
                if ( searchGroup.quickSearchGroup && !searchCriterion.quickSearchCriterion ) {
                    widgetIdsToShowExtendedCriteria.push( criterion.field );
                }

                // 2. es KEINE Quicksearch Gruppe ist und darin ein nicht quicksearch Feld ist, für das Werte gespeichert wurden
                if ( !searchGroup.quickSearchGroup && !searchCriterion.quickSearchCriterion ) {
                    widgetIdsToShowExtendedCriteria.push( criterion.field );
                    isValueInNonQuicksearchGroup = true;
                }
            }
        }

        // Informs all widgets that should be displayed of that fact and remembers them for future changes.
        this.searchFormData.setWidgetsToDisplayFromSearchSet( this.widgetGroups
            .flatMap( widgetGroup => widgetGroup.widgets )
            .filter( widget => widgetIdsToShowExtendedCriteria.includes( widget.id ) ) );

        return isValueInNonQuicksearchGroup;
    }

    private setWidgetGroups( search: HisSearch, searchCriteriaSetToLoad: HisSearchCriteriaSet ) {
        console.log( 'his-search-save-params.component#setWidgetGroups() #search', search );

        const lazyLoadedWidgetAssignments: Map<HisSelectWidget | HisMultiSelectWidget, HisSearchCriteria> = new Map();

        for ( let loadedGroup of search.groups ) {

            // Hier findet das Füllen der Werte in das Formular statt
            // in search-form geht das über die Params und die Vorbelegung der Widgets
            for ( let loadedCriteria of loadedGroup.criteria ) {
                if ( loadedCriteria.value === null || loadedCriteria.value === undefined ) {
                    continue;
                }

                // console.log('his-search-save-params.component#setWidgetGroups() #loadedCriteria', loadedCriteria);
                let widgetToSet = HisWidgetUtils.getWidgetWithIdFromWidgetGroups( loadedCriteria.field, this.widgetGroups );
                // console.log('his-search-save-params.component#setWidgetGroups() #widgetToSet', widgetToSet);
                // console.log('his-search-save-params.component#setWidgetGroups() #this.widgetGroups', this.widgetGroups);

                if ( this.isLazyLoadingWidget( widgetToSet ) ) {
                    lazyLoadedWidgetAssignments.set( widgetToSet, loadedCriteria );
                    continue;
                }

                this.setCriteriaValue( loadedCriteria, widgetToSet );
            }

            this.searchFormData.copyWidgetsFromGroupAndCreateCriteriaGroup( loadedGroup, false );
            this.searchForm.populateLazyWidgetValues( search );
        }

        const finishedLoadingObservable: Observable<void> = this.setLazyWidgetValues( lazyLoadedWidgetAssignments, search );

        console.log( 'his-search-save-params.component#setWidgetGroups() #this.widgetGroups', this.widgetGroups );
        /* Nicht ladbare Suchkriterien ermitteln */

        finishedLoadingObservable.subscribe(
            () => {
                this.findNotLoadedCriteriaAndTriggerMessage( this.widgetGroups, searchCriteriaSetToLoad );
            } );
    }

    private isLazyLoadingWidget( widget: HisWidgetBase<any> ): widget is HisSelectWidget | HisMultiSelectWidget {
        if ( !( widget instanceof HisSelectWidget ) && !( widget instanceof HisMultiSelectWidget ) ) {
            return false;
        }

        // was ist hier, wenn es ein Multiselect ist?
        if ( GenericSearchUtil.isLazySelectFrom( widget.optionsKey, widget.showDropdownIcon ) ) {
            return true;
        }

        if ( !widget.quickSearch ) {
            return true;
        }

        return false;
    }

    private setLazyWidgetValues( lazyLoadedWidgetAssignments: Map<HisSelectWidget | HisMultiSelectWidget, HisSearchCriteria>, search: HisSearch ): Observable<void> {
        console.log( 'his-search-save-params.component#setLazyWidgetValues() #lazyLoadedWidgetAssignments', lazyLoadedWidgetAssignments.keys() );

        const optionByIdRequests: OptionByIdRequest[] = [];
        const nullOptions = new Map<HisSelectWidget | HisMultiSelectWidget, HisOption>();

        for ( let widgetAssignment of lazyLoadedWidgetAssignments.keys() ) {
            const criterion = lazyLoadedWidgetAssignments.get( widgetAssignment );
            this.searchFormData.triggerOperator( criterion.not, widgetAssignment );

            const values = criterion.value.split( ',' ).map( v => v.trim() ).filter( v => !!v );
            const realValues = values.filter( v => v !== 'ISNULL' );

            // ISNULL ist ein Sentinel und im Backend nicht auflösbar (#372905)
            if ( realValues.length < values.length ) {
                const nullOption = new HisOption();
                nullOption.id = 'ISNULL';
                nullOption.label = this.hisUiService.translate( 'genericSearch.nullValueMenuEntry' );
                nullOptions.set( widgetAssignment, nullOption );
                this.setNewWidgetValue( widgetAssignment, nullOption );
            }

            if ( realValues.length > 0 ) {
                optionByIdRequests.push( new OptionByIdRequest( widgetAssignment.optionsKey, realValues ) );
            } else {
                lazyLoadedWidgetAssignments.delete( widgetAssignment );
            }
        }

        if ( optionByIdRequests.length == 0 ) {
            return of( undefined ); // triggers next when subscribed to
        }

        return this.searchService.loadOptionsByIds( search.configFile, optionByIdRequests ).pipe(
            tap( result => {
                if ( !result ) {
                    return;
                }

                for ( let selectedOption of result ) {
                    if ( !selectedOption || !selectedOption.selectedOptions || selectedOption.selectedOptions.length == 0 ) {
                        continue;
                    }
                    const matchedWidget = Array.from( lazyLoadedWidgetAssignments.keys() ).find( widget => widget.optionsKey === selectedOption.selectFrom );
                    if ( !matchedWidget ) {
                        continue;
                    }

                    if ( matchedWidget instanceof HisMultiSelectWidget ) {
                        const nullOption = nullOptions.get( matchedWidget );
                        const optionsToSet = nullOption
                            ? [nullOption, ...selectedOption.selectedOptions]
                            : [...selectedOption.selectedOptions];
                        this.setNewWidgetValue( matchedWidget, optionsToSet );
                    } else {
                        this.setNewWidgetValue( matchedWidget, selectedOption.selectedOptions[0] );
                    }
                }
            } ),
            map( (): undefined => undefined ) //effectively returns 'of(undefined)' where 'next' is triggered when the pipe recieves a value
        );
    }

    /** Queues a call to `showMessageCouldNotLoadAllCriteria()` to prevent duplicate notifications */
    private queueMessageCouldNotLoadAllCriteria() {
        this.couldNotLoadAllCriteriaMessageBuffer$.next();
    }

    private showMessageCouldNotLoadAllCriteria() {
        this.notificationService.warn(
            this.hisUiService.translate( 'genericSearch.searchCriteriaSets.applyProblem' ),
            this.hisUiService.translate( 'view.messages.warning' ) );
    }

    private setCriteriaValue( criterion: HisSearchCriteria, widgetToSet: HisWidgetBase<any> ) {
        if ( widgetToSet instanceof HisWidgetBaseSelect ) {
            this.searchFormData.triggerOperator( criterion.not, widgetToSet as HisWidgetBaseSelect<any> );
            let isLidSearch = GenericSearchUtil.isLidPath( widgetToSet.id );

            // (nicht gefüllt)/(value not existing), see #325731
            if ( criterion.value && criterion.value === 'ISNULL' ) {
                let option = new HisOption();
                option.id = criterion.value;
                let nullValueLabel = this.hisUiService.translate( 'genericSearch.nullValueMenuEntry' );
                option.label = nullValueLabel;
                this.setNewWidgetValue( widgetToSet, option );
                return;
            }

            widgetToSet.getWidgetValueDetail( criterion.value, isLidSearch ).subscribe( optionsToSet => {
                if ( optionsToSet.value ) {
                    this.setNewWidgetValue( widgetToSet, optionsToSet.value );
                }
                if ( optionsToSet.hasUnresolvedId ) {
                    this.queueMessageCouldNotLoadAllCriteria();
                }
            } );
        } else if ( widgetToSet instanceof HisOptionWidget ) {
            if ( criterion.value ) {
                // console.log('his-search-save-params.component#setWidgetGroups() #checkbox value', loadedCriteria.value);
                // console.log('his-search-save-params.component#setWidgetGroups() #widgetToSet', widgetToSet);
                widgetToSet.option = criterion.value;
                this.setNewWidgetValue( widgetToSet, true );
            }
        } else {
            // Eingabefeld aktualisieren, value: string
            if ( widgetToSet.id !== criterion.value ) {
                // console.log('his-search-save-params.component#setWidgetGroups() #input, date', loadedCriteria.value);
                // console.log('his-search-save-params.component#setWidgetGroups() #widgetToSet', widgetToSet);
                this.setNewWidgetValue( widgetToSet, criterion.value );
            }
        }
    }

    /**
     * prüft, ob es criteria gibt, die nicht geladen werden kann,
     * etwa weil sich die Suchkonfiguration geändert hat und das
     * erwartete Feld nicht mehr im Suchformular enthalten ist
     * oder ein Option nicht mehr vorhanden ist
     *
     * @param loadedWidgetGroups    die aufgrund des Suchsets geladenen widgetGroups,
     *                              ohne diejenigen, die nicht mehr in der Suchkonfiguration enthalten sind
     *
     * @param searchCriteriaSetToLoad das Suchset, das geladen werden soll mit den eventuell nicht ladbaren Suchkriterien
     *
     * @return array von HisSearchSetCriteria, die nicht geladen werden konnten
     */
    findNotLoadedCriteriaAndTriggerMessage(
        loadedWidgetGroups: HisWidgetGroup[], searchCriteriaSetToLoad: HisSearchCriteriaSet ) {
        for ( let group of searchCriteriaSetToLoad.search.groups ) {
            for ( let criterion of group.criteria ) {
                this.findNotLoadedCriterionAndTriggerMessage( criterion, loadedWidgetGroups );
            }
        }
    }

    private findNotLoadedCriterionAndTriggerMessage( criteria: HisSearchCriteria, loadedWidgetGroups: HisWidgetGroup[] ) {
        console.log( 'his-search-save-params.component#findNotLoadedCriterion() #criteria', criteria );
        let widget = HisWidgetUtils.getWidgetWithIdFromWidgetGroups( criteria.field, loadedWidgetGroups ) as HisWidgetBase<any>;

        if ( widget === null || widget === undefined ) {
            return;
        }
        if ( widget instanceof HisSelectWidget && this.isLazyLoadingWidget( widget ) ) {
            //Lazy
            if ( widget.value.id !== criteria.value && criteria.value !== 'ISNULL' ) {
                this.queueMessageCouldNotLoadAllCriteria();
                return;
            }
        } else if ( widget instanceof HisMultiSelectWidget && this.isLazyLoadingWidget( widget ) ) {
            // ACHTUNG: Wenn hier ein Wert ausgewählt ist,
            // dann wird dieser aus der Liste der verfügbaren Werte entfernt (aus den options)

            // Außerdem muss beachtet werden, dass criteria.value hier mehrere Werte enthalten kann
            let criteriaValues: string[] = criteria.value.split( ',' )
                .filter( v => v !== 'ISNULL' ); // ISNULL ist ein gültiger Sentinel-Wert, keine echte ID (#372905)

            if ( criteriaValues.length === 0 ) {
                return;
            }

            widget.options.subscribe( ( opts: HisOption[] ) => {
                if ( opts && opts.length > 0 ) {
                    let optIds = HisWidgetUtils.getOptionIds( opts );
                    // console.log( 'opts =', opts, ', optIds =', optIds );
                    let notExistentValues = difference( criteriaValues, optIds );
                    console.log( 'criteriaValues =', criteriaValues, ', notExistentValues =', notExistentValues );
                    if ( notExistentValues.length > 0 ) {
                        this.queueMessageCouldNotLoadAllCriteria();
                        return;
                    }
                }
            } );

        } else if ( widget instanceof HisSearchSelectWidget ) {
            // Wo kommen die Options für HisSearchSelect her?
            // Im Service steht: options for autocomplete in his-search-select are loaded in the component,
            // filtered by the autocomplete input
            // widget.values hat jedoch HisOptions[]

            let widgetValues: string[] = widget.value.toString().split( ',' );
            let criteriaValues: string[] = criteria.value.split( ',' );

            // XXX Der folgende Ausdruck gibt niemals 'true' weil widget.value asynchron geladen wird und hier noch [] ist.
            if ( HisArrayUtils.containsAll( criteriaValues, widgetValues ) ) {
                return;
            }

            // hier habe ich keine options, das widget kennt diesel nicht
            // also holen, eventuell gibt es die auch schon, aber wo?:
            let filteredSelectList: HisOption[] = [];

            // suchen ohne Einschränkung
            let wherePart = '';

            // hier sollten möglichst alle Options gefunden werden
            let limit = 1000;
            this.searchService.loadFilteredOptions( widget.searchConfigFile, wherePart, limit ).subscribe(
                ( result: HisOption[] ) => {
                    if ( result ) {
                        filteredSelectList = result;
                        let filteredSelectListIds = [];
                        for ( let option of filteredSelectList ) {
                            filteredSelectListIds.push( option.id );
                        }

                        let notContainedValues = difference( criteriaValues, filteredSelectListIds );
                        console.log( 'not contained values', notContainedValues );
                        if ( notContainedValues.length > 0 ) {
                            this.queueMessageCouldNotLoadAllCriteria();
                            return;
                        }
                    }
                }
            );

        } else if ( widget instanceof HisTreeSelectWidget ) {
            // Achtung: Lid Behandlung
            widget.treeSubject.subscribe( ( tree: TreeNode[] ) => {
                if ( tree ) {
                    // Baum-Elemente, die inkl. Unterbaum ausgewählt wurden,
                    // haben ein x an der id hängen, z.B. "3x", (siehe auch #250963).
                    // console.log( 'widget', widget.id, ': tree =', tree );
                    let isLidSearch = GenericSearchUtil.isLidPath( widget.id );
                    let treeNodeIds = HisTreeSelectWidget.getAllTreeNodeIds( tree, isLidSearch );
                    // console.log('his-search-save-params.component#findNotLoadedCriterion() #treeNodeIds', treeNodeIds);
                    let treeNodeIdsAsStringArray: string[] = treeNodeIds.map( value => String( value ) );
                    let criteriaValues: string[] = criteria.value.split( ',' );
                    let notContainedValues = this.getNotContainedTreeNodeElements( criteriaValues, treeNodeIdsAsStringArray );
                    // console.log('his-search-save-params.component#findNotLoadedCriterion() #notContainedValues', notContainedValues);
                    if ( notContainedValues.length > 0 ) {
                        this.queueMessageCouldNotLoadAllCriteria();
                        return;
                    }
                }
            } );

        } else {
            // input/checkbox/date: nichts machen
        }
    }

    getNotContainedTreeNodeElements( criteriaValues: string[], treeNodeIdsAsStringArray: string[] ) {
        // console.log('his-search-save-params.component#getNotContainedTreeNodeElements() #criteriaValues', criteriaValues);
        if ( criteriaValues === null || criteriaValues === undefined ) {
            throw new Error( 'array1 must not be null or undefined' );
        }
        if ( treeNodeIdsAsStringArray === null || treeNodeIdsAsStringArray === undefined ) {
            throw new Error( 'array2 must not be null or undefined' );
        }

        let notContainedElements = [];
        for ( let criteriaValue of criteriaValues ) {
            // Baum-Elemente, die inkl. Unterbaum ausgewählt wurden,
            // haben ein x an der id hängen, z.B. "3x", (siehe auch #250963).
            let value = criteriaValue.replace( 'x', '' );

            if ( !treeNodeIdsAsStringArray.includes( value ) ) {
                notContainedElements.push( criteriaValue );
            }
        }
        return notContainedElements;
    }

    /**
     * Setzt den Wert des Widgets mit der Id 'widgetToSet.id' sowohl im Ui
     * als auch im Model für alle Felder ausser für Checkboxen.
     *
     * @param widgetToSet Das Widget, dass aktualisiert werden soll
     * @param loadedValue Der Wert der dem Widget.value übergeben werden soll
     */
    private setNewWidgetValue( widgetToSet: HisWidgetBase<any>, loadedValue?: any ) {
        // Achtung, böse Falle:
        // Das Ui und das Model verwenden teilweise unterschiedliche Variablen

        if ( loadedValue === null || loadedValue === undefined ) {
            return;
        }

        // Multi-Select erwartet immer ein Array
        if ( widgetToSet instanceof HisMultiSelectWidget && !Array.isArray( loadedValue ) ) {
            loadedValue = [loadedValue];
        }

        // Ui aktualisieren
        widgetToSet.value = loadedValue;

        // Model aktualisieren
        this.form.value[widgetToSet.id] = loadedValue;
        this.form.controls[widgetToSet.id].setValue( loadedValue );
        this.form.controls[widgetToSet.id].markAsDirty();

        // original-Werte vor Inputs aktualisieren:
        // Im Fall von Array-Werten brauchen wir unterschiedliche Objekte,
        // damit Änderungen an value nicht gleich Änderungen bei originalValue sind
        widgetToSet.originalValue = Array.isArray( loadedValue ) ? [...loadedValue] : loadedValue;
        console.log( 'set', widgetToSet.id, 'originalValue to', widgetToSet.originalValue );
    }

    private getDefaultSearchCriteriaSetIfPresent(): HisSearchCriteriaSet {
        for ( let criteriaSet of this.allSearchCriteriaSets ) {
            if ( criteriaSet.standard === true && criteriaSet.defaulttext.endsWith( HisSearchSaveParamsComponent.STANDARD ) ) {
                return criteriaSet;
            }
        }
        return undefined;
    }

    setAsStandard() {
        this.notificationService.clear();
        let currentSet = this.stateInfo.currentCriteriaSet;
        if ( currentSet != null && currentSet.id != null ) {

            // Die Daten in der Suchmaske können sich ändern, deshalb werden hier nochmal die aktuellen Daten abgefragt
            currentSet.standard = true;

            let search = HisSearchParserHelper.createNewHisSearchByWidgetValues( this.widgetGroups, this.configFile );
            currentSet.search = search;

            let otherStandardCriteriaSet = this.getDefaultSearchCriteriaSetIfPresent();
            this.searchCriteriaSetService.updateSearchCriteriaSet( currentSet ).subscribe(
                ( newSavedSearchCriteriaSet: HisSearchCriteriaSet ) => {
                    if ( newSavedSearchCriteriaSet != null ) {
                        // Neues Hinzufügen Anfang - das betrifft nur das Ui,
                        // die Objekte in der Datenbank werden vom Backend verwaltet
                        this.initCurrentSearchCriteriaSetsAndRemoveById( currentSet.id );
                        this.initAllSearchCriteriaSetsAndRemoveById( currentSet.id );

                        this.selectedSearchSet = newSavedSearchCriteriaSet;
                        this.selectedSearchSet.defaulttext = newSavedSearchCriteriaSet.defaulttext + HisSearchSaveParamsComponent.STANDARD;

                        this.stateInfo.currentCriteriaSet = this.selectedSearchSet;

                        this.storeSearchSetAtSessionStorage( newSavedSearchCriteriaSet );

                        // erst pushen dann löschen wegen der Initialisierung
                        this.allSearchCriteriaSets.push( this.selectedSearchSet );
                        this.currentSearchCriteriaSets.push( currentSet );

                        this.notificationService.success(
                            this.hisUiService.translate( 'cs.sys.portal.portlet.portalmessages.save' ),
                            this.hisUiService.translate( 'view.messages.success' ) );

                        // Neues Hinzufügen Ende
                        this.isShowSetStandardIcon = false;
                        this.isShowResetStandardIcon = true;
                    }
                }
            );

            this.removeStandardSetFromUi( otherStandardCriteriaSet );
            this.setWidgetOriginalValueFromWidgetValue( false );
        }
    }

    private removeStandardSetFromUi( standardCriteriaSetToRemove: HisSearchCriteriaSet ) {
        if ( standardCriteriaSetToRemove !== undefined ) {
            // Bisheriges Suchset entfernen - das betrifft nur das Ui,
            // die Objekte in der Datenbank werden vom Backend verwaltet
            this.initCurrentSearchCriteriaSetsAndRemoveById( standardCriteriaSetToRemove.id );
            this.initAllSearchCriteriaSetsAndRemoveById( standardCriteriaSetToRemove.id );

            standardCriteriaSetToRemove.defaulttext = standardCriteriaSetToRemove.defaulttext.replace(
                HisSearchSaveParamsComponent.STANDARD, '' ).trim();
            standardCriteriaSetToRemove.standard = false;

            this.storeSearchSetAtSessionStorage( standardCriteriaSetToRemove );

            this.allSearchCriteriaSets.push( standardCriteriaSetToRemove );
            this.currentSearchCriteriaSets.push( standardCriteriaSetToRemove );
        }
    }

    resetStandard() {
        this.notificationService.clear();
        let currentSet = this.stateInfo.currentCriteriaSet;
        console.log( 'resetStandard #this.stateInfo', this.stateInfo );
        if ( currentSet != null && currentSet.id != null ) {

            let search = HisSearchParserHelper.createNewHisSearchByWidgetValues( this.widgetGroups, this.configFile );
            currentSet.search = search;

            currentSet.standard = false;
            currentSet.defaulttext = this.selectedSearchSet.defaulttext.replace( HisSearchSaveParamsComponent.STANDARD, '' ).trim();

            this.searchCriteriaSetService.updateSearchCriteriaSet( currentSet ).subscribe(
                ( savedSearchCriteriaSet: HisSearchCriteriaSet ) => {
                    if ( savedSearchCriteriaSet != null ) {
                        this.initCurrentSearchCriteriaSetsAndRemoveById( currentSet.id );
                        this.initAllSearchCriteriaSetsAndRemoveById( currentSet.id );

                        this.selectedSearchSet = savedSearchCriteriaSet;
                        this.stateInfo.currentCriteriaSet = this.selectedSearchSet;

                        this.allSearchCriteriaSets.push( currentSet );
                        this.currentSearchCriteriaSets.push( currentSet );

                        this.storeSearchSetAtSessionStorage( savedSearchCriteriaSet );

                        this.notificationService.success(
                            this.hisUiService.translate( 'cs.sys.portal.portlet.portalmessages.save' ),
                            this.hisUiService.translate( 'view.messages.success' ) );

                        this.isShowSetStandardIcon = true;
                        this.isShowResetStandardIcon = false;

                        this.setWidgetOriginalValueFromWidgetValue( false );
                    }
                }
            );
        }
    }

    cancel() {

        this.newSearchCriteriaSetName = '';

        // für alle
        this.isShowAddView = false;

        if ( this.allSearchCriteriaSets.length === 0 ) {
            this.showNoSearchSetExistsView();
            return;
        }

        if ( this.allSearchCriteriaSets.length > 0 && this.selectedValueBeforCancel != null ) {
            this.selectedSearchSet = this.selectedValueBeforCancel;
            this.stateInfo.currentCriteriaSet = this.selectedValueBeforCancel;
            this.stateInfo.stateType = HisSearchCriteriaSetStateType.CANCELED;

            this.showSelectView();
            this.isShowSetStandardIcon = !this.selectedSearchSet.standard;
            this.isShowResetStandardIcon = this.selectedSearchSet.standard;
            this.selectedValueBeforCancel = null;
            return;
        }

        if ( this.allSearchCriteriaSets.length > 0 && this.selectedValueBeforCancel == null ) {
            this.showSearchSetExistsButNothingSelectedView();
            this.selectedValueBeforCancel = null;
            return;
        }
    }

    delete() {
        this.notificationService.clear();
        let currentSet = this.selectedSearchSet;
        if ( this.stateInfo.currentCriteriaSet ) {
            currentSet = this.stateInfo.currentCriteriaSet;
        }

        this.confirmationService.confirm( {
            key: CONFIRM_PDIALOG_KEY,
            message: this.hisUiService.translate(
                'genericSearch.searchCriteriaSet.deleteQuestion',
                currentSet.defaulttext ),
            accept: () => {

                this.searchCriteriaSetService.deleteSearchCriteriaSet( currentSet.id ).subscribe( ( deleteSuccess: number ) => {

                    if ( deleteSuccess === 1 ) {
                        // Delete Success Message
                        this.notificationService.success(
                            this.hisUiService.translate( 'de.his.javang.successfullySaved' ),
                            this.hisUiService.translate( 'view.messages.success' ) );

                        this.initAllSearchCriteriaSetsAndRemoveById( currentSet.id );
                        this.initCurrentSearchCriteriaSetsAndRemoveById( currentSet.id );

                        // Anfang Empty Option entfernen
                        // Wenn das Suchset das letzte Suchset ist,
                        // das nicht emptyOption ist, dann muss auch emptyOption gelöscht werden

                        if ( this.allSearchCriteriaSets.length === 1 && this.allSearchCriteriaSets[0].emptyOption ) {
                            this.allSearchCriteriaSets = [];
                        }

                        if ( this.currentSearchCriteriaSets.length === 1 && this.currentSearchCriteriaSets[0].emptyOption ) {
                            this.currentSearchCriteriaSets = [];
                        }

                        // Ende Empty Option entfernen

                        this.selectedSearchSet = null; // clear selectedValue

                        // eigentlich ist nun emptyOption ausgewählt
                        this.stateInfo.currentCriteriaSet = null;
                        this.stateInfo.stateType = HisSearchCriteriaSetStateType.DELETED;

                        sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET );

                        if ( this.allSearchCriteriaSets.length === 0 ) {
                            this.showNoSearchSetExistsView();
                        } else {
                            this.showSearchSetExistsButNothingSelectedView();
                        }
                    }
                } );
            }
        } );
    }

    private initAllSearchCriteriaSetsAndRemoveById( id: number ) {
        this.allSearchCriteriaSets = []; // ohne Neu-Initialisierung funktioniert es nicht
        for ( let criteriaSet of this.currentSearchCriteriaSets ) {
            if ( criteriaSet.id !== id ) {
                this.allSearchCriteriaSets.push( criteriaSet );
            }
        }
    }

    private initCurrentSearchCriteriaSetsAndRemoveById( id: number ) {
        this.currentSearchCriteriaSets = []; // ohne Neu-Initialisierung funktioniert es nicht
        for ( let criteriaSet of this.allSearchCriteriaSets ) {
            if ( criteriaSet.id !== id ) {
                this.currentSearchCriteriaSets.push( criteriaSet );
            }
        }
    }

    public completeMethod( event: { query: string } ) {
        this.currentSearchCriteriaSets = []; // ohne Neu-Initialisierung funktioniert es nicht
        for ( let searchCriteriaSet of this.allSearchCriteriaSets ) {
            this.currentSearchCriteriaSets.push( searchCriteriaSet );
        }

        const autocompleteQuery = ( event.query || '' ).toLowerCase();

        if ( !autocompleteQuery ) {
            return;
        }

        this.currentSearchCriteriaSets = ( this.currentSearchCriteriaSets || [] ).filter( item =>
            ( item.defaulttext || '' ).toLowerCase().includes( autocompleteQuery )
        );
    }

    onClear() {
        this.selectedSearchSet = null;
        this.currentSearchCriteriaSets = this.currentSearchCriteriaSets || [];
    }

    private isChangeSearch(): boolean {
        return sessionStorage.getItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON ) === 'CHANGE_SEARCH';
    }

    private isNewSearch(): boolean {
        return sessionStorage.getItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON ) === 'NEW_SEARCH';
    }

    private isNavigation() {
        return !this.isChangeSearch() && !this.isNewSearch();
    }

    private getStandardSet(): HisSearchCriteriaSet {
        for ( let searchSet of this.allSearchCriteriaSets ) {
            if ( searchSet.standard ) {
                return searchSet;
            }
        }
        return null;
    }

    /**
     * Setzt die ermittelten Suchsets initial in {@code allSearchCriteriaSets}
     * und {@code currentSearchCriteriaSets}, damit diese im Auswahlfeld angezeigt werden können.
     *
     * <p>Falls es ein Standard Suchset gibt, wird an dessen Namen der Zusatz <Standard> angehängt.</p>
     * <p>Falls das Array mit den ermittelten Suchsets nicht leer ist, wird eine leere Option hinzugefügt</p>
     *
     * @param foundSearchCriteriaSets die ermittelten Suchsets
     */
    private setSearchSets( foundSearchCriteriaSets: HisSearchCriteriaSet[] ) {

        if ( foundSearchCriteriaSets.length < 1 ) {
            return;
        }

        this.allSearchCriteriaSets = [];

        let emptyOptionSet = new HisSearchCriteriaSet();
        emptyOptionSet.emptyOption = true;

        this.allSearchCriteriaSets.push( emptyOptionSet );

        for ( let set of foundSearchCriteriaSets ) {
            if ( set.standard ) {
                set.defaulttext = set.defaulttext + HisSearchSaveParamsComponent.STANDARD;
            }
            this.allSearchCriteriaSets.push( set );
        }

        this.currentSearchCriteriaSets = [];
        for ( let set of this.allSearchCriteriaSets ) {
            this.currentSearchCriteriaSets.push( set );
        }
    }

    /**
     * Holt aufgrund der in {@code searchCriteriaSetRequest} übermittelten Daten die Suchsets aus der Datenbank
     * und setzt diese initial in {@code allSearchCriteriaSets} und {@code currentSearchCriteriaSets},
     * damit diese im Auswahlfeld angezeigt werden können.
     *
     * <p>In dieser Methode wird initial der Zustand der Anzeige der Suchsets beim Betreten der Seite geregelt.</p>
     *
     * @param searchCriteriaSetRequest ein {@link HisSearchCriteriaSetRequest} mit Daten zum Ermitteln von Suchsets in der Datenbank
     */
    private getSearchCriteriaSetsAndRegulateTheInitialView( searchCriteriaSetRequest: HisSearchCriteriaSetRequest ): void {
        this.searchCriteriaSetService.getSearchCriteriaSets( searchCriteriaSetRequest ).subscribe(
            ( allSearchCriteriaSets: HisSearchCriteriaSet[] ) => {

                this.setSearchSets( allSearchCriteriaSets );

                if ( this.isNewSearch() ) {
                    this.searchService.setResetInputs( true );
                    this.stateInfo.currentCriteriaSet = null;
                    this.isDataArrived = true;
                    sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON );
                    sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET );

                    if ( !this.currentSearchCriteriaSets || this.currentSearchCriteriaSets.length === 0 ) {
                        this.showNoSearchSetExistsView();
                    } else {
                        this.showSearchSetExistsButNothingSelectedView();
                    }
                    return;
                }

                // Eintrittspunkt Navigation
                if ( this.isNavigation() ) {
                    console.log(
                        'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView() #isNavigation',
                        this.isNavigation() );
                    // Es gibt noch keine Suchsets
                    if ( allSearchCriteriaSets === null || allSearchCriteriaSets === undefined || allSearchCriteriaSets.length === 0 ) {
                        this.showNoSearchSetExistsView();
                        this.isDataArrived = true;
                        return;
                    }

                    let standardSearchSet = this.getStandardSet();
                    // Es gibt Suchsets aber keine Standard Set
                    if ( !standardSearchSet ) {
                        this.showSearchSetExistsButNothingSelectedView();
                        this.isDataArrived = true;
                        return;
                    }

                    // Es gibt ein StandardSuchset, und dieser enthält bereits die Sucheinschränkungen, z.B.
                    // searchCriteria = [{ path: "[Role].id", value: "1,100054" }, { path: "EXT:[Role].id(NOT)", value: "true" }]
                    // console.log( 'standardSearchSet =', standardSearchSet );
                    if ( standardSearchSet ) {
                        this.searchCriteriaSetService.loadCriteriaSet( standardSearchSet.id ).subscribe(
                            ( search: HisSearch ) => {
                                sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON );
                                this.storeSearchSetAtSessionStorage( standardSearchSet );
                                sessionStorage.setItem( HisSessionStorageKeys.CS_SYS_SEARCH_CRITERIA_SET_COMPONENT_ID, this.componentId );

                                console.log( 'HisSearchSaveParams: load standard search set with data:', search );

                                // Die Standard Sets werden nun im ngOnInit in his-search-form behandelt (validateInputs, submitSearchForm)
                                // Deshalb müssen hier nur noch die richtigen Werte und Einstellungen gesetzt werden

                                //  this.searchService.validateInputs(search).subscribe(resultCode => {
                                //     if (resultCode === 0) {
                                //         console.log(
                                //             'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView()',
                                //             '#resultCode', resultCode);
                                // Falls im Suchet params gespeichert sind, für die kein Suchergebnis anzeigt wird.
                                // XXX Das geht performanter, wenn man resultCode aus
                                // this.searchFormData.submitSearch( search ) bezieht

                                // 1. Wert in die Widget setzen
                                let params = HisSearchParserHelper.convertHisSearchToFlatObjectFormatParams( search );
                                this.searchFormData.doPresetInputFields( params );
                                // 2. Suchset anzeigen
                                this.selectedSearchSet = standardSearchSet;
                                this.stateInfo.currentCriteriaSet = standardSearchSet;
                                this.showSelectView();
                                this.isShowSetStandardIcon = false;
                                this.isShowResetStandardIcon = true;
                                // 3. OriginalWerte setzen (nicht für Kriteriengruppen)
                                this.setWidgetOriginalValueFromSearchValue();
                                //                                }
                                //                            });
                                //                            this.searchFormData.submitSearch( search );
                                return;
                            }
                        );
                    }
                }

                if ( this.isChangeSearch() ) {
                    this.selectedSearchSet = this.getSearchSetFromSessionStorage();
                    sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON );

                    if ( this.allSearchCriteriaSets.length === 0 ) {
                        // Es ist changeSearch und es gibt noch kein Suchset
                        this.showNoSearchSetExistsView();
                        this.isDataArrived = true;
                        return;
                    }

                    // Es ist changeSearch und es gibt Suchsets (dann ist das Standard Suchset egal)

                    if ( this.selectedSearchSet === null || this.selectedSearchSet.emptyOption ) {
                        // Beim Durchführen der Suche war kein Suchset ausgewählt oder der ausgewählte Suchset war die leere Option
                        this.showSearchSetExistsButNothingSelectedView();
                        this.isDataArrived = true;
                        this.stateInfo.currentCriteriaSet = null;
                        return;
                    }

                    // Es ist changeSearch und ein Suchset war ausgewählt
                    this.showSelectView();
                    this.stateInfo.currentCriteriaSet = this.selectedSearchSet;
                    this.stateInfo.stateType = HisSearchCriteriaSetStateType.CHANGED_SEARCH;

                    // OriginalValues für Sucheinschränkungen aus dem Suchset neu setzen
                    // (alle anderen wurden manuell geändert)
                    // FIXME hier fehlt noch die Behandlung der Kriteriengruppen
                    this.setWidgetOriginalValueFromSearchValue();

                    // Ermitteln und Nachladen der Felder aus der erweiterten Suche, Anfang
                    // 1. Holen der Suchkonfiguration
                    // 2. Anreichern mit Values aus dem Suchset
                    // 3. Ermitten der Felder aus der erweiterten Suche
                    // 4. Nachladen der ggf. dafür benötigten Options
                    let originalSearchConfig = this.searchFormData.getSearchConfiguration();
                    // console.log(
                    // 'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView() #originalSearchConfig',
                    // originalSearchConfig );
                    let copyOfSearchConfig = this.getCopyOfSearchConfiguration( originalSearchConfig );
                    // console.log(
                    // 'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView() #copyOfSearchConfig',
                    // copyOfSearchConfig );

                    let search = this.stateInfo.currentCriteriaSet.search;
                    console.log( 'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView() #search', search );

                    // Die searchConfiguration braucht noch die values aus
                    for ( let searchGroup of this.stateInfo.currentCriteriaSet.search.groups ) {
                        for ( let group of copyOfSearchConfig.searchCriteria.group ) {
                            // console.log(
                            // 'his-search-save-params.component#getSearchCriteriaSetsAndRegulateTheInitialView() #searchGroup.groupLabel',
                            // searchGroup.groupLabel, '#group.label', group.label );
                            if ( searchGroup.groupLabel === group.label ) {
                                for ( let searchCriterion of searchGroup.criteria ) {
                                    for ( let criteria of group.criteria ) {
                                        if ( criteria.path === searchCriterion.field ) {
                                            criteria.value = searchCriterion.value;
                                        }
                                    }
                                }
                                // Kriteriengruppen? Wie ist das in JSF?
                                // Es wird nichts angezeigt, nicht die Gruppe und auch nicht das gespeicherte Feld.
                                // Wenn man die erweiterte Suche aufmacht, dann werdendie Werte an der Kriterienguppe angezeigt,
                                // diese kommenjedoch von 'Suche ändern'
                            }
                        }
                    }

                    // ermittelt, ob es Werte in der erweiterten Suche gibt
                    // Wenn ja, in searchForm component setzen, damit bei "Suche ändern" die Optionen geladen
                    // XXX Wenn wir "extendedSearch" im Suchset speichern könnten, dann müssten wir
                    // hier nicht einmal die Suchkonfiguration laden...

                    // XXX Für die Suchsets ist es nicht ausreichend, einmal extendedSearch = true zu machen
                    // 1. Es sollen nur diejenigen Gruppen angezeigt werden,
                    // in denen sich Suchfelder befinden, die in einem Suchset gespeichert sind.
                    // 2. Es sollen nur diejenigen Felder aus der erweiterten Suche angezeigt werden,
                    // die in einem Suchset gespeichert sind.
                    let isValueInNonQuicksearchGroup = this.evaluateExtendedSearch( copyOfSearchConfig, search );
                    console.log( 'isValueInNonQuicksearchGroup', isValueInNonQuicksearchGroup );
                    if ( isValueInNonQuicksearchGroup ) {
                        // hier werden nur die Options nachgeladen, das Anzeigen der Felder wird in his-search-form.html geregelt
                        this.widgetService.loadWidgetOptions( copyOfSearchConfig, true );
                    }
                    // Ermitteln und Nachladen der Felder aus der erweiterten Suche, Ende

                    this.isShowSetStandardIcon = !this.selectedSearchSet.standard;
                    this.isShowResetStandardIcon = this.selectedSearchSet.standard;
                    this.isDataArrived = true;
                    return;
                }

                // Tritt das überhaupt ein?
                console.warn(
                    'HisSearchSaveParamsComponent#getSearchCriteriaSetsAndRegulateTheInitialView',
                    '- Nicht berücksichtigter Fall beim initialen Einstellen des Suchsets' );
                // Vorsichtshalber nichts anzeigen
                this.showNoSearchSetExistsView();
                this.isDataArrived = true;
            }
        );
    }

    private setWidgetOriginalValueFromSearchValue() {
        // OriginalValues für Sucheinschränkungen aus dem Suchset neu setzen
        // (alle anderen wurden manuell geändert)
        for ( let searchGroup of this.selectedSearchSet.search.groups ) {
            let widgetGroup = HisWidgetUtils.getWidgetGroupWithLabel( searchGroup.groupLabel, this.widgetGroups );
            for ( let criterion of searchGroup.criteria ) {
                let widget = HisWidgetUtils.getWidgetWithIdFromGroup( criterion.field, widgetGroup );
                this.setOriginalValue( criterion.value, criterion.not, widget );
            }
        }
    }

    private setOriginalValue( rawValue: any, not: boolean, widget: HisWidgetBase<any> ) {
        if ( rawValue ) {
            if ( widget instanceof HisWidgetBaseSelect ) {
                widget.originalNot = not;
                let isLidSearch = GenericSearchUtil.isLidPath( widget.id );
                ( widget as HisWidgetBaseSelect<any> ).getWidgetValues( rawValue, isLidSearch ).subscribe( options => {
                    if ( options ) {
                        widget.originalValue = options;
                    }
                } );
            } else {
                widget.originalValue = rawValue;
            }
        }
    }

    private addEmptyOption() {
        let emptyOptionSet = new HisSearchCriteriaSet();
        emptyOptionSet.emptyOption = true;
        this.allSearchCriteriaSets.push( emptyOptionSet );
        this.currentSearchCriteriaSets.push( emptyOptionSet );
    }

    private getSearchSetFromSessionStorage(): HisSearchCriteriaSet {
        return JSON.parse( sessionStorage.getItem(
            HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET ) ) as HisSearchCriteriaSet;
    }

    private storeSearchSetAtSessionStorage( searchCriteriaSet: HisSearchCriteriaSet ) {
        sessionStorage.setItem(
            HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET, JSON.stringify( searchCriteriaSet ) );
    }

    private showNoSearchSetExistsView() {
        this.isShowDeleteIcon = false;
        this.isShowAddIcon = false;
        this.isShowSaveIcon = false;

        this.isShowSelectField = false;
        this.isShowAddLink = false;

        this.isShowAddView = false;

        this.isShowSetStandardIcon = false;
        this.isShowResetStandardIcon = false;
    }

    /**
     * Es sind Suchsets vorhanden, jedoch ist das Auswahlfeld leer,
     * weil die letzte Aktion z. B. das Löschen eines Suchsets war
     */
    private showSearchSetExistsButNothingSelectedView() {
        this.isShowDeleteIcon = false;
        this.isShowAddIcon = false;
        this.isShowSaveIcon = false;

        this.isShowSelectField = true;
        this.isShowAddLink = true;

        this.isShowAddView = false;

        this.isShowSetStandardIcon = false;
        this.isShowResetStandardIcon = false;
    }

    private showSelectView() {
        this.isShowDeleteIcon = true;
        this.isShowAddIcon = true;
        this.isShowSaveIcon = true;
        this.isShowSelectField = true;

        this.isShowAddLink = false;
        this.isShowAddView = false;
    }

    private resetInputs() {
        // log model (aka this.form.value) before reset
        console.log( 'Model before reset inputs =', this.form );

        // reset form inputs: triggers value changes that are noticed in his-search-form.component init()
        // Hier besser die parent component aufrufen
        this.form.reset();
    }

    /* Wenn der Button 'Eingaben zurücksetzen' gedrückt wird,
     * muss das Auswahlfeld für Suchsets die leere Option anzeigen*/
    private initializeResetInputs() {
        this.searchService.resetInputs.subscribe( ( resetInputs: boolean ) => {
            if ( resetInputs === true ) {
                // Wenn ein Suchset ausgewählt ist, wird vor dem Zurücksetzen der Auswahl
                // die Ansicht zurückgesetzt
                if ( this.selectedSearchSet !== null && this.selectedSearchSet !== undefined ) {
                    this.showSearchSetExistsButNothingSelectedView();
                }

                // Auswahlfeld
                this.selectedSearchSet = null;

                // Eingabefeld, falls ein neuese Suchset angelegt werden soll
                this.newSearchCriteriaSetName = null;

                this.stateInfo.currentCriteriaSet = null;
                this.stateInfo.stateType = null;

                // originalValues zurücksetzen
                this.setWidgetOriginalValueFromWidgetValue( true );
            }
        } );
    }
}
